import { useCallback, useEffect, useRef } from 'react'
import { useQueries, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { getErrorMessage } from '@features/shared/api/client'
import { useToast } from '@features/shared/components/Toast'
import { useSettingsStore } from '@features/settings/stores/useSettingsStore'
import { getExport } from '../api/exports.api'
import type { ExportJob } from '../api/contracts'
import { exportsQueryKeys } from '../api/queryKeys'
import {
  canSaveAs,
  downloadExport,
  EXPORT_QUICK_POLL_MS,
  exportPollMs,
  isExportFinished,
  openExport,
} from '../logic/exportJobs'
import { syncExports } from '../logic/exportJobCache'
import { openWhenFree } from '../logic/openWhenReady'
import { printExport } from '../logic/printExport'
import { shareExport, shareFile } from '../logic/shareExport'
import { useExportWatchStore, type WatchedExport } from '../stores/useExportWatchStore'
import { usePrintShortcut } from '../hooks/usePrintShortcut'

/** Text ending in a full stop, so a notice and the file's line read as two sentences. */
function sentence(text: string): string {
  const trimmed = text.trim()
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`
}

/**
 * Waits on the exports started in this browser: each is read every couple of
 * seconds until the worker finishes it. Then, with Download Automatically (on
 * unless the person turns it off), it downloads wherever they are in Arvoke and
 * a short message fades, since the browser shows its own; without it, the
 * message stays with Download (Save As). With Open When Ready, the nearest
 * Export menu opens too, once the person isn't typing or in a dialog. A print
 * opens the print dialog instead, and nothing downloads; where the page can't
 * print it (Safari), a message offers to open it to print. A share opens the
 * device's share sheet with the file, or, when it took longer than the
 * browser allows after a click, a message with Share. Lives in the app
 * shell, so leaving the page loses nothing, and so does Ctrl+P, which prints
 * the grid on the page (usePrintShortcut). Renders nothing.
 */
export default function ExportWatcher() {
  const watched = useExportWatchStore((state) => state.exports)
  const downloadAutomatically = useSettingsStore((state) => state.exportDownloadAutomatically)
  const openWhenReady = useSettingsStore((state) => state.exportOpenWhenReady)
  const preferencesRef = useRef({ downloadAutomatically, openWhenReady })
  preferencesRef.current = { downloadAutomatically, openWhenReady }
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  usePrintShortcut()

  const combine = useCallback(
    (results: UseQueryResult<ExportJob>[]) =>
      results.map((result) => ({ data: result.data, failed: result.isError })),
    []
  )
  const results = useQueries({
    queries: watched.map((job) => ({
      queryKey: exportsQueryKeys.job(job.exportId),
      queryFn: ({ signal }: { signal: AbortSignal }) => getExport(job.exportId, signal),
      refetchInterval: (query: { state: { data?: ExportJob } }) =>
        !query.state.data
          ? EXPORT_QUICK_POLL_MS
          : isExportFinished(query.state.data)
            ? false
            : exportPollMs([query.state.data]),
      refetchIntervalInBackground: true,
      retry: 2,
    })),
    combine,
  })

  useEffect(() => {
    // Each read moves its export forward in every list at once, so the
    // Export button's dot and the header's change together.
    syncExports(
      queryClient,
      results.flatMap((result) => (result.data ? [result.data] : []))
    )
    const { finish, endPrint, endShare } = useExportWatchStore.getState()
    // A print or share under way is over (the menu that started it closes).
    const handedOff = (job: WatchedExport) => {
      if (job.print) endPrint(job.scopeKey)
      if (job.share) endShare(job.scopeKey)
    }
    results.forEach((result, index) => {
      const job: WatchedExport | undefined = watched[index]
      if (!job) return
      if (result.failed) {
        if (finish(job.exportId)) handedOff(job)
        return
      }
      const data = result.data
      if (!data || !isExportFinished(data) || !finish(job.exportId)) return
      void queryClient.invalidateQueries({ queryKey: ['exports', 'list'] })
      if (data.status === 'failed') {
        handedOff(job)
        addToast({
          type: 'error',
          title: job.print ? "Couldn't print" : job.share ? "Couldn't share" : "Couldn't export",
          message: data.error ?? `${data.file_name} couldn't be built.`,
        })
        return
      }
      if (data.status !== 'done') {
        handedOff(job)
        return
      }
      if (job.share) {
        const failed = (error: unknown) =>
          addToast({ type: 'error', title: "Couldn't share", message: getErrorMessage(error) })
        // The browser opens a share sheet only soon after a click: a file
        // that took longer waits for one, already read.
        const offerToShare = (file: File) =>
          addToast({
            type: 'success',
            title: 'Ready to share',
            message: data.file_name,
            duration: 0,
            actions: [{ label: 'Share', onClick: () => void shareFile(file).catch(failed) }],
            dedupeKey: `export:${data.export_id}`,
          })
        shareExport(data)
          .then((shared) => {
            if (shared.outcome === 'needs-click') offerToShare(shared.file)
          })
          .catch(failed)
          .finally(() => endShare(job.scopeKey))
        return
      }
      if (job.print) {
        // Why the file isn't all that was asked for (Maximum Pages cut it).
        if (data.notice) {
          addToast({
            type: 'warning',
            title: 'Printing',
            message: sentence(data.notice),
            dedupeKey: `export:${data.export_id}`,
          })
        }
        const offerToOpen = () =>
          addToast({
            type: 'success',
            title: 'Ready to print',
            message: `Open ${data.file_name} and print it from there.`,
            duration: 0,
            actions: [
              {
                label: 'Open to print',
                onClick: () =>
                  void openExport(data).catch((error: unknown) =>
                    addToast({
                      type: 'error',
                      title: "Couldn't open",
                      message: getErrorMessage(error),
                    })
                  ),
              },
            ],
            dedupeKey: `export:${data.export_id}`,
          })
        printExport(data)
          .then((printed) => {
            if (!printed) offerToOpen()
          })
          .catch((error: unknown) =>
            addToast({ type: 'error', title: "Couldn't print", message: getErrorMessage(error) })
          )
          .finally(() => endPrint(job.scopeKey))
        return
      }

      // On its own it downloads as any file does; the toast's button is a
      // click, so it can ask where to save it (Chrome, Edge: "Save as…").
      const download = (saveAs: boolean) =>
        downloadExport(data, { saveAs }).catch((error: unknown) =>
          addToast({ type: 'error', title: "Couldn't download", message: getErrorMessage(error) })
        )
      const { downloadAutomatically: automatic, openWhenReady: openMenu } = preferencesRef.current
      if (automatic) void download(false)
      if (openMenu) openWhenFree(job.scopeKey)
      const fileLine = automatic ? `Downloaded ${data.file_name}` : data.file_name
      // Why the file isn't what was asked for (an Excel export that became a
      // CSV): it stays until it's read.
      const notice = data.notice ? sentence(data.notice) : null
      addToast({
        type: notice ? 'warning' : 'success',
        title: automatic ? 'Export complete' : 'Export ready',
        message: notice ? `${notice} ${sentence(fileLine)}` : fileLine,
        // The browser shows its own download message: Arvoke's is short and fades.
        duration: automatic && !notice ? undefined : 0,
        actions:
          automatic && !notice
            ? []
            : [
                {
                  label: canSaveAs() ? 'Save as…' : 'Download',
                  onClick: () => void download(true),
                },
              ],
        dedupeKey: `export:${data.export_id}`,
      })
    })
  }, [addToast, queryClient, results, watched])

  return null
}
